import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Alert, Button, Form, FormGroup, Input, Label } from 'reactstrap';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { useUpdateScanTunnelMutation } from '../../../graphql/mutations/updateScanTunnel.generated';
import {
  GetScanTunnelDocument,
  useGetScanTunnelQuery,
} from '../../../graphql/queries/getScanTunnel.generated';
import { ScanTunnelMode } from '../../../graphql/generatedTypes';
import { Communicator } from '../../../tools/Communication';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { DEFAULT_CONTAINER_TYPE_ICON } from '@/utils/imageUrl';
import { Routes } from '@/utils/routes';

type ContainerType = {
  id: number;
  name: string;
  imageUrl: string;
};

export const ScanTunnelEditPage = () => {
  const { scanTunnelId } = useParams<{ scanTunnelId: string }>();
  const history = useHistory();
  const {
    data: scanTunnelData,
    loading: queryLoading,
    error: errorLoadingScanTunnel,
  } = useGetScanTunnelQuery({ variables: { id: scanTunnelId } });
  const [name, setName] = useState('');
  const [mode, setMode] = useState(ScanTunnelMode.App);
  const [containerTypeId, setContainerTypeId] = useState(1);
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const [
    updateScanTunnel,
    { loading: updateLoading, error: errorUpdatingScanTunnel },
  ] = useUpdateScanTunnelMutation();

  useEffect(() => {
    if (scanTunnelData?.scanTunnel) {
      setName(scanTunnelData.scanTunnel.name || '');
      setMode(scanTunnelData.scanTunnel.mode);
      setContainerTypeId(scanTunnelData.scanTunnel.container_type_id || 1);
    }
  }, [scanTunnelData]);

  useEffect(() => {
    Communicator.Get('api/3/containers/getAllContainerTypes', {})
      .then((response) => response.json())
      .then((data) => {
        setContainerTypes(data);
      });
  }, []);

  function getContainerTypeInfo() {
    const type = containerTypes.find((ct) => ct.id === (containerTypeId || 1));
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <p style={{ marginRight: '10px' }}>Scan Tunnel Container Type:</p>
        <img
          style={{ width: 40, height: 40, marginRight: '10px' }}
          src={type?.imageUrl || DEFAULT_CONTAINER_TYPE_ICON}
          alt=""
        />
        {type?.id} - {type?.name}
      </div>
    );
  }

  const handleUpdateScanTunnel = async () => {
    if (!name || !mode) {
      LayoutStoreInstance.setError('Name and mode is required');
      return;
    }
    const result = await updateScanTunnel({
      variables: {
        id: scanTunnelId,
        input: {
          name,
          mode,
          container_type_id: containerTypeId,
        },
      },
      refetchQueries: [
        {
          query: GetScanTunnelDocument,
          variables: { id: scanTunnelId },
        },
      ],
      onCompleted: () => history.push(Routes.scanTunnel(scanTunnelId)),
    });
    if (result.errors) {
      LayoutStoreInstance.setError('Error updating scan tunnel');
    } else {
      LayoutStoreInstance.setSuccess('Scan tunnel updated');
    }
  };

  if (errorLoadingScanTunnel) return <p>Error loading scan tunnel</p>;

  if (queryLoading) return <p>Loading...</p>;

  return (
    <div>
      <h2>Edit Scan Tunnel</h2>
      <Form>
        <FormGroup>
          <Label for="scanTunnelName">Name</Label>
          <Input
            type="text"
            name="name"
            id="scanTunnelName"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <Label for="scanTunnelMode">Mode</Label>
          <Input
            type="select"
            name="mode"
            id="scanTunnelMode"
            placeholder="Name"
            value={mode}
            onChange={(e) => setMode(e.target.value as ScanTunnelMode)}
          >
            {Object.entries(ScanTunnelMode).map(([key, value]) => (
              <option key={value} value={value}>
                {key}
              </option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup style={{ display: 'flex', justifyContent: 'space-between' }}>
          {getContainerTypeInfo()}
          <Dropdown isOpen={dropdownOpen} toggle={toggle}>
            <DropdownToggle caret>Select Container Type</DropdownToggle>
            <DropdownMenu>
              {containerTypes.map((ct) => (
                <DropdownItem
                  key={ct.id}
                  onClick={() => {
                    setContainerTypeId(ct.id);
                  }}
                >
                  <img
                    style={{ width: 30, height: 30 }}
                    src={ct.imageUrl || DEFAULT_CONTAINER_TYPE_ICON}
                    alt={ct.name}
                  />
                  {ct.id} - {ct.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </FormGroup>
        {errorUpdatingScanTunnel && (
          <Alert color="danger">Error: {errorUpdatingScanTunnel.message}</Alert>
        )}
        <Button
          color="primary"
          onClick={handleUpdateScanTunnel}
          disabled={updateLoading}
        >
          Update Scan Tunnel
        </Button>
      </Form>
    </div>
  );
};
